import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { Shield, Trash2, RotateCcw, Copy, Plus, X, KeyRound, BookOpen } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { Avatar } from "../lib/helpers";
import { useAuth } from "../context/AuthContext";
import { ConfirmDialog } from "../components/ConfirmDialog";

const TABS = [
  { id: "general", l: "General" },
  { id: "users", l: "Usuarios" },
  { id: "progress", l: "Progreso" },
  { id: "teams", l: "Equipos" },
  { id: "departments", l: "Departamentos" },
  { id: "trash", l: "Papelera" },
  { id: "integrations", l: "Integraciones" },
];
const inputCls = "w-full bg-[#0A0A0B] border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-primary";

export default function AdminPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("general");
  const [orgName, setOrgName] = useState("");
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [trash, setTrash] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [newToken, setNewToken] = useState(null);
  const [feedUrl, setFeedUrl] = useState("");
  const [invName, setInvName] = useState("");
  const [invEmail, setInvEmail] = useState("");
  const [invited, setInvited] = useState(null);
  const [resetInfo, setResetInfo] = useState(null);
  const [confirmCfg, setConfirmCfg] = useState(null);
  const [teamName, setTeamName] = useState("");
  const [teamDept, setTeamDept] = useState("");
  const [departments, setDepartments] = useState([]);
  const [depName, setDepName] = useState("");
  const [progressData, setProgressData] = useState([]);

  const load = useCallback(async () => {
    try {
      const [s, u, t, tr, tk, d, pr] = await Promise.all([
        api.get("/settings"), api.get("/users"), api.get("/teams"), api.get("/trash"), api.get("/tokens"), api.get("/departments"), api.get("/admin/user-progress"),
      ]);
      setOrgName(s.data.org_name); setUsers(u.data); setTeams(t.data); setTrash(tr.data); setTokens(tk.data); setDepartments(d.data); setProgressData(pr.data);
    } catch (e) { console.error("[admin] no se pudieron cargar los datos", e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (user?.role !== "admin") return <Navigate to="/" replace />;

  const saveOrg = async () => { await api.put("/admin/settings", { org_name: orgName }); toast.success("Guardado"); };
  const setRole = async (uid, role) => {
    try { await api.put(`/admin/users/${uid}/role`, { role }); load(); toast.success("Rol actualizado"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const invite = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/invite", { name: invName, email: invEmail });
      setInvited(data); setInvName(""); setInvEmail(""); load();
    } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
  };
  const createTeam = async () => {
    if (!teamName.trim()) return;
    await api.post("/teams", { name: teamName, department: teamDept, member_ids: [] });
    setTeamName(""); setTeamDept(""); load();
  };
  const toggleTeamMember = async (team, uid) => {
    const ids = team.member_ids.includes(uid) ? team.member_ids.filter((x) => x !== uid) : [...team.member_ids, uid];
    await api.put(`/teams/${team.id}`, { name: team.name, department: team.department, member_ids: ids });
    load();
  };
  const createDep = async (e) => {
    e.preventDefault();
    if (!depName.trim()) return;
    try {
      await api.post("/departments", { name: depName });
      setDepName(""); load(); toast.success("Departamento creado");
    } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
  };
  const deleteDep = async (id) => {
    await api.delete(`/departments/${id}`);
    load();
  };
  const restore = async (id) => {
    try { await api.post(`/trash/${id}/restore`); toast.success("Restaurado"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const purge = async (id) => { await api.delete(`/trash/${id}`); load(); };
  const createPat = async () => {
    const { data } = await api.post("/tokens", { name: "Token API" });
    setNewToken(data.token); load();
  };
  const getFeed = async () => {
    const { data } = await api.post("/me/calendar-feed");
    setFeedUrl(process.env.REACT_APP_BACKEND_URL + data.feed_path);
  };
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copiado"); };

  return (
    <div className="h-screen overflow-y-auto">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <h1 className="font-display text-3xl font-extrabold flex items-center gap-2" data-testid="admin-title"><Shield className="h-7 w-7 text-primary" /> Administración</h1>
          <a href="/guias/guia-administrador.pdf" target="_blank" rel="noreferrer" data-testid="admin-guide-link"
            className="inline-flex items-center gap-2 text-sm font-semibold bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2 transition-colors">
            <BookOpen className="h-4 w-4 text-primary" /> Guía del administrador (PDF)
          </a>
        </div>
        <div className="flex gap-1 border-b border-white/10 mb-6 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`text-sm font-semibold px-4 py-2.5 border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === t.id ? "border-primary text-white" : "border-transparent text-zinc-500 hover:text-white"}`}>{t.l}</button>
          ))}
        </div>

        {tab === "general" && (
          <div className="max-w-md space-y-4" data-testid="admin-general">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Nombre de la organización</label>
              <input data-testid="org-name-input" value={orgName} onChange={(e) => setOrgName(e.target.value)} className={`${inputCls} mt-1.5`} />
            </div>
            <button onClick={saveOrg} data-testid="save-org-button" className="bg-primary hover:bg-[#FF5047] text-white font-semibold px-4 py-2 rounded-lg text-sm">Guardar</button>
          </div>
        )}

        {tab === "users" && (
          <div className="space-y-6" data-testid="admin-users">
            <form onSubmit={invite} className="flex flex-wrap gap-2 items-end bg-[#121214] border border-white/10 rounded-xl p-4">
              <div className="flex-1 min-w-[140px]">
                <label className="text-xs text-zinc-500">Nombre</label>
                <input data-testid="invite-name-input" required value={invName} onChange={(e) => setInvName(e.target.value)} className={`${inputCls} mt-1`} />
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs text-zinc-500">Correo (@galletasaromaticas.com)</label>
                <input data-testid="invite-email-input" required type="email" value={invEmail} onChange={(e) => setInvEmail(e.target.value)} className={`${inputCls} mt-1`} />
              </div>
              <button type="submit" data-testid="invite-submit" className="bg-primary hover:bg-[#FF5047] text-white font-semibold px-4 py-2 rounded-lg text-sm flex items-center gap-1.5"><Plus className="h-4 w-4" /> Invitar</button>
            </form>
            {invited && (
              <div className="bg-[#00E676]/10 border border-[#00E676]/30 rounded-xl p-4 text-sm" data-testid="invite-result">
                <p className="font-semibold text-[#00E676]">Usuario creado: {invited.user.email}</p>
                <p className="text-zinc-300 mt-1 flex items-center gap-2">Contraseña temporal: <code className="bg-black/40 px-2 py-0.5 rounded">{invited.temp_password}</code>
                  <button onClick={() => copy(invited.temp_password)}><Copy className="h-3.5 w-3.5 text-zinc-400 hover:text-white" /></button></p>
                <p className="text-xs text-zinc-500 mt-1">Compártela de forma segura. Podrá cambiarla desde su perfil.</p>
              </div>
            )}
            {resetInfo && (
              <div className="bg-[#FFD600]/10 border border-[#FFD600]/30 rounded-xl p-4 text-sm" data-testid="reset-result">
                <p className="font-semibold text-[#FFD600]">Contraseña restablecida: {resetInfo.email}</p>
                <p className="text-zinc-300 mt-1 flex items-center gap-2">Nueva contraseña temporal: <code className="bg-black/40 px-2 py-0.5 rounded">{resetInfo.temp_password}</code>
                  <button onClick={() => copy(resetInfo.temp_password)}><Copy className="h-3.5 w-3.5 text-zinc-400 hover:text-white" /></button></p>
                <p className="text-xs text-zinc-500 mt-1">Compártela de forma segura. Sus sesiones anteriores quedaron cerradas y podrá cambiarla desde su perfil.</p>
              </div>
            )}
            <div className="space-y-2">
              {users.map((u, ui) => (
                <div key={u.id} className="flex items-center gap-3 bg-[#121214] border border-white/10 rounded-xl px-4 py-3 animate-fade-up" style={{ animationDelay: `${Math.min(ui * 50, 450)}ms` }} data-testid={`admin-user-${u.id}`}>
                  <Avatar user={u} size={34} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{u.name}</p>
                    <p className="text-xs text-zinc-500 truncate">{u.email}</p>
                  </div>
                  {u.id === user.id ? <span className="text-xs font-semibold text-primary">Tú (admin)</span> : (
                    <>
                      <button onClick={() => setConfirmCfg({
                        title: "Restablecer contraseña",
                        message: `Se generará una nueva contraseña temporal para ${u.email} y sus sesiones actuales se cerrarán.`,
                        confirmLabel: "Restablecer",
                        tone: "warn",
                        action: async () => {
                          try {
                            const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
                            setResetInfo(data);
                          } catch { toast.error("No se pudo restablecer"); }
                        },
                      })} data-testid={`reset-password-${u.id}`} title="Restablecer contraseña"
                        className="text-xs text-zinc-500 hover:text-[#FFD600] border border-white/10 hover:border-[#FFD600]/40 rounded-lg px-2.5 py-1.5 flex items-center gap-1 transition-colors">
                        <KeyRound className="h-3.5 w-3.5" /> Restablecer
                      </button>
                      <select value={u.role} onChange={(e) => setRole(u.id, e.target.value)} data-testid={`user-role-${u.id}`}
                        className="bg-[#0A0A0B] border border-white/10 rounded-lg text-xs px-2 py-1.5 outline-none">
                        <option value="member">Miembro</option>
                        <option value="admin">Admin</option>
                      </select>
                      <button onClick={() => setConfirmCfg({
                        title: "Eliminar usuario",
                        message: `¿Eliminar a ${u.name} (${u.email})? Se quitará de todos los tableros y tareas. Esta acción no se puede deshacer.`,
                        confirmLabel: "Eliminar usuario",
                        tone: "danger",
                        action: async () => {
                          try {
                            await api.delete(`/admin/users/${u.id}`);
                            toast.success("Usuario eliminado");
                            load();
                          } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
                        },
                      })} data-testid={`delete-user-${u.id}`} title="Eliminar usuario"
                        className="text-zinc-600 hover:text-red-400 border border-white/10 hover:border-red-400/40 rounded-lg p-2 transition-colors">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "progress" && (
          <div className="space-y-4" data-testid="admin-progress">
            <p className="text-sm text-zinc-500">Avance, en porcentaje, de todas las tareas creadas por cada usuario.</p>
            {progressData.map((row, ri) => (
              <div key={row.user.id} className="bg-[#121214] border border-white/10 rounded-xl p-4 animate-fade-up" style={{ animationDelay: `${Math.min(ri * 60, 420)}ms` }} data-testid={`user-progress-${row.user.id}`}>
                <div className="flex items-center gap-3 mb-1">
                  <Avatar user={row.user} size={34} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{row.user.name}</p>
                    <p className="text-xs text-zinc-500">{row.total} tarea{row.total !== 1 ? "s" : ""} creada{row.total !== 1 ? "s" : ""}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-display text-xl font-extrabold" data-testid={`avg-progress-${row.user.id}`}>{row.avg_progress}%</p>
                    <p className="text-[10px] text-zinc-500 uppercase tracking-wider">promedio</p>
                  </div>
                </div>
                <div className="h-1.5 bg-white/5 rounded-full overflow-hidden mb-3">
                  <div className="h-full rounded-full" style={{ width: `${row.avg_progress}%`, background: row.avg_progress >= 100 ? "#00E676" : "#00B0FF" }} />
                </div>
                {row.tasks.length > 0 ? (
                  <div className="space-y-1.5">
                    {row.tasks.map((t) => (
                      <div key={t.id} className="flex items-center gap-3 bg-[#0A0A0B] rounded-lg px-3 py-2" data-testid={`progress-task-${t.id}`}>
                        <span className="h-2 w-2 rounded-full shrink-0" style={{ background: t.status_color }} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm truncate">{t.title}</p>
                          <p className="text-[11px] text-zinc-600 truncate">{t.board_name} · {t.status_title}</p>
                        </div>
                        <div className="w-24 sm:w-32 h-1.5 bg-white/5 rounded-full overflow-hidden shrink-0">
                          <div className="h-full rounded-full" style={{ width: `${t.progress}%`, background: t.is_done ? "#00E676" : "#00B0FF" }} />
                        </div>
                        <span className={`text-xs font-bold w-10 text-right shrink-0 ${t.is_done ? "text-[#00E676]" : ""}`}>{t.progress}%</span>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-xs text-zinc-600">Sin tareas creadas.</p>}
              </div>
            ))}
          </div>
        )}

        {tab === "teams" && (
          <div className="space-y-4" data-testid="admin-teams">
            <div className="flex flex-wrap gap-2 items-end bg-[#121214] border border-white/10 rounded-xl p-4">
              <div className="flex-1 min-w-[140px]">
                <label className="text-xs text-zinc-500">Equipo</label>
                <input data-testid="team-name-input" value={teamName} onChange={(e) => setTeamName(e.target.value)} className={`${inputCls} mt-1`} />
              </div>
              <div className="flex-1 min-w-[140px]">
                <label className="text-xs text-zinc-500">Departamento</label>
                <input data-testid="team-dept-input" value={teamDept} onChange={(e) => setTeamDept(e.target.value)} className={`${inputCls} mt-1`} />
              </div>
              <button onClick={createTeam} data-testid="create-team-button" className="bg-primary hover:bg-[#FF5047] text-white font-semibold px-4 py-2 rounded-lg text-sm flex items-center gap-1.5"><Plus className="h-4 w-4" /> Crear</button>
            </div>
            {teams.map((t, ti) => (
              <div key={t.id} className="bg-[#121214] border border-white/10 rounded-xl p-4 animate-fade-up" style={{ animationDelay: `${Math.min(ti * 60, 420)}ms` }} data-testid={`team-${t.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-semibold">{t.name}</p>
                    {t.department && <p className="text-xs text-[#00B0FF]">{t.department}</p>}
                  </div>
                  <button onClick={async () => { await api.delete(`/teams/${t.id}`); load(); }} className="text-zinc-600 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {users.map((u) => {
                    const inTeam = t.member_ids.includes(u.id);
                    return (
                      <button key={u.id} onClick={() => toggleTeamMember(t, u.id)} data-testid={`team-${t.id}-member-${u.id}`}
                        className={`flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-full border text-xs transition-all ${inTeam ? "border-primary bg-primary/10" : "border-white/10 hover:border-white/25 opacity-60"}`}>
                        <Avatar user={u} size={20} /> {u.name.split(" ")[0]}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            {teams.length === 0 && <p className="text-sm text-zinc-600">Sin equipos todavía.</p>}
          </div>
        )}

        {tab === "departments" && (
          <div className="space-y-4 max-w-xl" data-testid="admin-departments">
            <p className="text-sm text-zinc-500">Da de alta los departamentos de la organización. Podrás asignarlos a las tareas y verlos en la vista "Mis tareas".</p>
            <form onSubmit={createDep} className="flex gap-2 items-end bg-[#121214] border border-white/10 rounded-xl p-4">
              <div className="flex-1">
                <label className="text-xs text-zinc-500">Nombre del departamento</label>
                <input data-testid="department-name-input" value={depName} onChange={(e) => setDepName(e.target.value)} placeholder="Ej: Producción, Ventas, RRHH…" className={`${inputCls} mt-1`} />
              </div>
              <button type="submit" data-testid="create-department-button" className="bg-primary hover:bg-[#FF5047] text-white font-semibold px-4 py-2 rounded-lg text-sm flex items-center gap-1.5"><Plus className="h-4 w-4" /> Crear</button>
            </form>
            <div className="space-y-2">
              {departments.map((d, di) => (
                <div key={d.id} className="flex items-center gap-3 bg-[#121214] border border-white/10 rounded-xl px-4 py-3 animate-fade-up" style={{ animationDelay: `${Math.min(di * 50, 450)}ms` }} data-testid={`department-${d.id}`}>
                  <span className="h-2.5 w-2.5 rounded-full bg-[#00B0FF]" />
                  <p className="flex-1 text-sm font-semibold">{d.name}</p>
                  <button onClick={() => deleteDep(d.id)} data-testid={`delete-department-${d.id}`} className="text-zinc-600 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              {departments.length === 0 && <p className="text-sm text-zinc-600">Sin departamentos todavía.</p>}
            </div>
          </div>
        )}

        {tab === "trash" && (
          <div className="space-y-2" data-testid="admin-trash">
            {trash.length === 0 && <p className="text-sm text-zinc-600">La papelera está vacía.</p>}
            {trash.map((i, ii) => (
              <div key={i.id} className="flex items-center gap-3 bg-[#121214] border border-white/10 rounded-xl px-4 py-3 animate-fade-up" style={{ animationDelay: `${Math.min(ii * 50, 450)}ms` }} data-testid={`trash-${i.id}`}>
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${i.type === "board" ? "bg-[#00B0FF]/15 text-[#00B0FF]" : "bg-[#FFD600]/15 text-[#FFD600]"}`}>{i.type === "board" ? "Tablero" : "Tarea"}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{i.name}</p>
                  <p className="text-xs text-zinc-500">Eliminado por {i.deleted_by_name} · {new Date(i.deleted_at).toLocaleString("es", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                </div>
                <button onClick={() => restore(i.id)} data-testid={`restore-${i.id}`} className="flex items-center gap-1 text-xs font-semibold text-[#00E676] hover:bg-[#00E676]/10 px-2.5 py-1.5 rounded-lg"><RotateCcw className="h-3.5 w-3.5" /> Restaurar</button>
                <button onClick={() => purge(i.id)} data-testid={`purge-${i.id}`} className="text-zinc-600 hover:text-red-400"><X className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}

        {tab === "integrations" && (
          <div className="space-y-6 max-w-xl" data-testid="admin-integrations">
            <div className="bg-[#121214] border border-white/10 rounded-xl p-4">
              <h3 className="font-semibold mb-1">Tokens de API (REST)</h3>
              <p className="text-xs text-zinc-500 mb-3">Usa el token como Bearer en la cabecera Authorization para integrar cualquier sistema con la API.</p>
              {newToken && (
                <div className="bg-[#00E676]/10 border border-[#00E676]/30 rounded-lg p-3 mb-3 text-xs flex items-center gap-2" data-testid="new-token">
                  <code className="flex-1 break-all">{newToken}</code>
                  <button onClick={() => copy(newToken)}><Copy className="h-4 w-4 text-zinc-400 hover:text-white" /></button>
                </div>
              )}
              <div className="space-y-1.5 mb-3">
                {tokens.map((t, ki) => (
                  <div key={t.id} className="flex items-center gap-2 text-xs text-zinc-400 animate-fade-up" style={{ animationDelay: `${Math.min(ki * 50, 400)}ms` }}>
                    <code className="bg-black/40 px-2 py-0.5 rounded">{t.token}</code>
                    <span className="flex-1">{t.name}</span>
                    <button onClick={async () => { await api.delete(`/tokens/${t.id}`); load(); }} className="text-zinc-600 hover:text-red-400">Revocar</button>
                  </div>
                ))}
              </div>
              <button onClick={createPat} data-testid="create-token-button" className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-sm">Generar token</button>
            </div>
            <div className="bg-[#121214] border border-white/10 rounded-xl p-4">
              <h3 className="font-semibold mb-1">Google Calendar / Outlook (feed ICS)</h3>
              <p className="text-xs text-zinc-500 mb-3">Suscríbete a este calendario desde Google Calendar u Outlook para ver tus tareas con vencimiento.</p>
              {feedUrl ? (
                <div className="bg-black/40 rounded-lg p-3 text-xs flex items-center gap-2" data-testid="feed-url">
                  <code className="flex-1 break-all">{feedUrl}</code>
                  <button onClick={() => copy(feedUrl)}><Copy className="h-4 w-4 text-zinc-400 hover:text-white" /></button>
                </div>
              ) : (
                <button onClick={getFeed} data-testid="get-feed-button" className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-sm">Obtener URL del calendario</button>
              )}
            </div>
            <div className="bg-[#121214] border border-white/10 rounded-xl p-4">
              <h3 className="font-semibold mb-1">Slack / Discord / Teams / Webhooks</h3>
              <p className="text-xs text-zinc-500">Configúralos por tablero desde <b>Automatizaciones ⚡</b>: acción "Enviar a Slack/Discord/Teams" o "Llamar webhook" con la URL del webhook entrante de tu canal.</p>
            </div>
          </div>
        )}
      </div>
      {confirmCfg && (
        <ConfirmDialog
          title={confirmCfg.title}
          message={confirmCfg.message}
          confirmLabel={confirmCfg.confirmLabel}
          tone={confirmCfg.tone}
          onConfirm={async () => { const a = confirmCfg.action; setConfirmCfg(null); await a(); }}
          onCancel={() => setConfirmCfg(null)}
        />
      )}
    </div>
  );
}
